/**
 * ### QueryMatcher
 *
 * @description
 * Function that determines if a query's arguments match certain criteria
 */
type QueryMatcher<TArgs = Record<string, unknown>> = (args: TArgs) => boolean;

/**
 * ### CachedQuery
 *
 * @description
 * Represents a cached RTK Query with its metadata
 */
interface CachedQuery {
  endpointName?: string;
  originalArgs?: Record<string, unknown>;
}

/**
 * ### FindMatchingQueriesOptions
 *
 * @description
 * Options for finding matching cached queries
 */
interface FindMatchingQueriesOptions<TArgs extends Record<string, unknown>> {
  /** The queries object from RTK Query state */
  queries: Record<string, unknown> | undefined;
  /** The endpoint name to filter by */
  endpointName: string;
  /** Function to test if query arguments match criteria */
  matcher: QueryMatcher<TArgs>;
}

/**
 * ### findMatchingCachedQueries
 *
 * @description
 * Finds all cached queries that match a specific endpoint and argument criteria.
 * This is useful for updating multiple cached queries in optimistic updates.
 *
 * @example
 * const matches = findMatchingCachedQueries({
 *   queries,
 *   endpointName: 'getProjectsByProjectIdSurveyConfigs',
 *   matcher: (args) => args.projectId === 123
 * });
 *
 * @param options - The options for finding matching queries
 * @returns Array of validated arguments for matching queries
 */
const findMatchingCachedQueries = <TArgs extends Record<string, unknown>>(
  options: FindMatchingQueriesOptions<TArgs>,
): TArgs[] => {
  const { queries, endpointName, matcher } = options;

  // Early return if no queries exist
  if (!queries || Object.keys(queries).length === 0) {
    return [];
  }

  const matchingArgs: TArgs[] = [];

  // Use for...of with Object.entries for better performance
  for (const [, query] of Object.entries(queries)) {
    const cachedQuery = query as CachedQuery;

    if (cachedQuery?.endpointName === endpointName && cachedQuery?.originalArgs) {
      const args = cachedQuery.originalArgs as TArgs;
      if (matcher(args)) {
        matchingArgs.push(args);
      }
    }
  }

  return matchingArgs;
};

export default findMatchingCachedQueries;
